import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateModule } from '@angular/material-moment-adapter';
/******************* */
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef, MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { StoreModule } from '@ngrx/store';
//---------------Services-------------------//
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { IPublicClientApplication, InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { CookieService } from 'ngx-cookie-service';
import { NgxSpinnerModule } from 'ngx-spinner';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { lastValueFrom } from 'rxjs';
import { AppComponent } from './app.component';
import { HeaderComponent } from './components/header/header.component';
import { LandingComponent } from './components/landing/landing/landing.component';
import { LeftnavmenuComponent } from './components/leftnavmenu/leftnavmenu.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { OneDigitDecimalNumberDirective } from './directives/onedigitdecimalnumber.directive';
import { TooltipDirective } from './directives/tooltip.directive';
import { TwoDigitDecimalNumberDirective } from './directives/twodigitdecimalnumber.directive';
import { JwtInterceptor } from './helper/jwt.interceptor';
import AlarmTemplateAddCanDeactivateGuard from './services/alarm-template/alarm-deactivate.guard';
import { AuthService } from './services/auth/auth.service';
import { EntityService } from './services/entity/entity.service';
import { NotificationService } from './services/notification/notification.service';
import { NotificationCountService } from './services/notification/notificationCountService.service';
import { FilterPipe } from './filter.pipe';
import { RoleService } from './services/role/role.service';
import UserAuditCanDeactivateGuard from './services/user-audit/user-audit-deactivate.guard';
import { machineListFilterReducer } from './store/machine-list-filter/machine-list-filter.reducer';
import { FooterModule } from './components/footer/footer.module';
import { DialogTemplatesModule } from './components/common/dialog-templates.module';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { SidebarToggleButtonModule } from './components/sidebar-toggle-button/sidebar-toggle-button.module';
import { UiElementsModule } from './components/ui-elements/ui-elements.module';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { ToastrModule } from 'ngx-toastr';
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS } from '@angular/material/autocomplete';
import { NgxMatDatetimePickerModule, NgxMatNativeDateModule } from '@nng-components/angular-material-datetime-picker';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Material from '@primeng/themes/material';

export const protectedResourceMap: [string, string[]][] = [[environment.msal.costaExpressUrl, [environment.msal.apiscope]]];

const globalRippleConfig: RippleGlobalOptions = { disabled: true };

const tooltipDefaults: MatTooltipDefaultOptions = {
  showDelay: 300,
  hideDelay: 100,
  touchendHideDelay: 0,
};

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msal.clientId,
      authority: environment.msal.authority,
      redirectUri: environment.msal.redirectUrl,
      postLogoutRedirectUri: environment.msal.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: 'localStorage',
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.msal.costaExpressUrl, [environment.msal.apiscope]); // Prod environment. Uncomment to use.

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [environment.msal.loginscope],
    },
  };
}

export function HttpLoaderFactory(httpClient: HttpClient) {
}

export function initializeMsalFactory(msalService: MsalService): () => Promise<void> {
  return () => lastValueFrom(msalService.initialize()).catch((err) => {
    // Prevent APP_INITIALIZER from blocking app indefinitely if initialize fails.
    // Log the error and allow the app to continue. Adjust behavior if you want to fail fast.
    console.error('MsalService.initialize() failed during APP_INITIALIZER', err);
    return Promise.resolve();
  });
}

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    LeftnavmenuComponent,
    TooltipDirective,
    TwoDigitDecimalNumberDirective,
    OneDigitDecimalNumberDirective,
    LandingComponent,
    PageNotFoundComponent,
    FilterPipe,
  ],
  exports: [TooltipDirective, TwoDigitDecimalNumberDirective, OneDigitDecimalNumberDirective],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    CommonModule,
    NgMultiSelectDropDownModule.forRoot(),
    BrowserAnimationsModule,
    NgxSpinnerModule,
    AppRoutingModule,
    MatButtonModule,
    MatDialogModule,
    StoreModule.forRoot({ machineListFilter: machineListFilterReducer }, {}),
    FooterModule,
    DialogTemplatesModule,
    MatMomentDateModule,
    MatNativeDateModule,
    NgxMatDatetimePickerModule,
    NgxMatNativeDateModule,
    SidebarToggleButtonModule,
    UiElementsModule,
    ToastrModule.forRoot()
  ],
  providers: [
    AuthService,
    EntityService,
    CookieService,
    RoleService,
    NotificationService,
    NotificationCountService,
    UserAuditCanDeactivateGuard,
    AlarmTemplateAddCanDeactivateGuard,
    MatMomentDateModule,
    MatDatepickerModule,
    NgxMatDatetimePickerModule,
    NgxMatNativeDateModule,
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsalFactory,
      deps: [MsalService],
      multi: true,
    },
    { provide: MatDialogRef, useValue: {} },
    { provide: MAT_DIALOG_DATA, useValue: {} },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: {
        appearance: 'outline',
        subscriptSizing: 'dynamic',
      },
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: {
        hasBackdrop: true,
      },
    },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 5000, verticalPosition: 'top' } },
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: tooltipDefaults },
    {
      provide: MAT_TABS_CONFIG,
      useValue: {
        animationDuration: '250ms',
        disablePagination: false,
        dynamicHeight: true,
      },
    },
    { provide: MAT_SELECT_CONFIG, useValue: { disableOptionCentering: 'false', overlayPanelClass: 'gd2-select-panel' } },
    { provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS, useValue: { overlayPanelClass: 'gd2-select-panel' } },
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Material,
        options: {
          darkModeSelector: 'none',
        },
      },
      ripple: false,
    }),
  ],
})
export class AppModule {
}
